/**
 * @fileoverview Dedicated department-wide migration report (success / failed / resume).
 * @module system-web/pages/migration/LegacyMigrationRunDetailPage
 *
 * Responsibilities:
 * - Show one department-wide run without crowding the Occupancies grid.
 * - List per-dataset created / updated / failed and grouped failure reasons.
 * - Resume remaining datasets on the same runId; re-run a failed dataset as its own job.
 * - Present a run-centric department dashboard with effective latest-attempt totals and remaining work.
 * - Preserve original parent results and every linked dataset rerun in visible attempt history.
 * - Stop / restart a single-dataset rerun without rewriting the historical parent document.
 *
 * Dependencies:
 * - react-router-dom
 * - lucide-react
 * - getMigrationRun / resumeRun / startRun / cancelRun from legacyMigrationService
 * - departmentWideStepper grouping helpers
 *
 * Architecture:
 * - Local-first Dexie read via getMigrationRun; liveQuery on the same runId.
 * - Resume / re-run go through the service (never apiClient in the page).
 *
 * Notes:
 * - Pre-run review of Occupancies blockers is the Grid band filter (except Documents).
 * - Apply-time transform/load errors only exist after the write; they appear here.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { liveQuery } from 'dexie';
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { getSystemDb } from '@firesquirrel/local-db';
import {
  cancelRun,
  fetchAndSyncRunById,
  getMigrationRun,
  listChildDatasetRunsForParent,
  resumeRun,
  startRun,
  type MigrationRunRow,
  type MigrationRunStepRow,
} from '../../services/legacyMigrationService';
import {
  formatMigrationFailureForAdmin,
  formatRehostProgressLine,
  formatStepProgressLine,
  groupDepartmentWideStepsByTier,
  isCompletedWithRowFailures,
  isMigrationRunHeartbeatStale,
  isStepHeartbeatStale,
  overlayRehostFromChildRuns,
  overlayStepWithChildRuns,
  summarizeEffectiveDepartmentMigration,
} from '../../services/departmentWideStepper';
import { telemetry } from '../../services/telemetry';
import {
  buildDatasetGridNavState,
  displayNameForDatasetKey,
  resolveFailedStepRetrySelection,
} from '../../services/legacyMigrationRunNav';

/**
 * @component LegacyMigrationRunDetailPage
 * Purpose: Full-page report for one department-wide apply.
 * Props: none (route param runId)
 * Behavior: liveQuery the parent and linked child attempts; latest child result replaces the
 *           dataset's effective status while all attempt IDs remain visible.
 * Data Source: local (Dexie)
 * Constraints: no apiClient
 * Performance: one run document; failedRows capped server-side at 2000
 */
export default function LegacyMigrationRunDetailPage() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const [run, setRun] = useState<MigrationRunRow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [childAttemptsByAdapter, setChildAttemptsByAdapter] = useState<Record<string, MigrationRunRow[]>>({});
  const [retryingKey, setRetryingKey] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 15_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!runId) {
      setRun(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    let subscription: { unsubscribe: () => void } | null = null;
    setIsLoading(true);
    getSystemDb()
      .then((db) => {
        if (cancelled) return;
        void db;
        subscription = liveQuery(() => getMigrationRun(runId)).subscribe({
          next: (row) => {
            if (cancelled) return;
            setRun(row);
            setIsLoading(false);
          },
          error: (err: unknown) => {
            if (cancelled) return;
            setError(err instanceof Error ? err.message : 'Failed to load run');
            setIsLoading(false);
          },
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to open Dexie');
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, [runId]);

  useEffect(() => {
    if (!runId || run?.cancelRequested || (run && run.status !== 'running' && run.status !== 'pending')) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      await fetchAndSyncRunById(runId);
      const latest = await getMigrationRun(runId);
      if (!cancelled && latest) setRun(latest);
    };
    void poll();
    const id = setInterval(() => { void poll(); }, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [runId, run?.status, run?.cancelRequested]);

  useEffect(() => {
    if (!run?.tenantId || run.isGlobal === false || !run.steps?.length) {
      setChildAttemptsByAdapter({});
      return;
    }
    let cancelled = false;
    const subscription = liveQuery(() => listChildDatasetRunsForParent(
      run.tenantId,
      run.steps!.map((step) => step.adapterKey),
      run.startedAt,
      run._id,
    )).subscribe({
      next: (attempts) => {
        if (!cancelled) setChildAttemptsByAdapter(attempts);
      },
      error: (err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load dataset attempts');
      },
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [run?._id, run?.tenantId, run?.startedAt, run?.isGlobal, run?.steps]);

  const liveChildRunIds = useMemo(
    () => Object.values(childAttemptsByAdapter)
      .flat()
      .filter((child) => child.status === 'running' || child.status === 'pending')
      .map((child) => child._id)
      .sort(),
    [childAttemptsByAdapter],
  );

  useEffect(() => {
    if (liveChildRunIds.length === 0 || !run?.tenantId || !run.steps?.length) return;
    let cancelled = false;
    const poll = async () => {
      await Promise.all(liveChildRunIds.map((childRunId) => fetchAndSyncRunById(childRunId)));
      const latest = await listChildDatasetRunsForParent(
        run.tenantId,
        run.steps!.map((step) => step.adapterKey),
        run.startedAt,
        run._id,
      );
      if (!cancelled) setChildAttemptsByAdapter(latest);
    };
    void poll();
    const id = setInterval(() => { void poll(); }, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [liveChildRunIds.join('|'), run?._id, run?.tenantId, run?.startedAt, run?.steps]);

  const latestChildByAdapter = useMemo(
    () => Object.fromEntries(Object.entries(childAttemptsByAdapter).map(([key, attempts]) => [key, attempts[0]])),
    [childAttemptsByAdapter],
  );
  const latestDatasetAttempt = useMemo(
    () => Object.values(latestChildByAdapter)
      .filter((attempt): attempt is MigrationRunRow => Boolean(attempt))
      .sort((a, b) => String(b.startedAt).localeCompare(String(a.startedAt)))[0],
    [latestChildByAdapter],
  );
  const effectiveSteps = useMemo(
    () => (run?.steps ?? []).map((step) => overlayStepWithChildRuns(step, childAttemptsByAdapter[step.adapterKey] ?? [])),
    [run?.steps, childAttemptsByAdapter],
  );
  const tiers = useMemo(
    () => groupDepartmentWideStepsByTier(effectiveSteps),
    [effectiveSteps],
  );
  const effectiveSummary = useMemo(
    () => summarizeEffectiveDepartmentMigration(effectiveSteps),
    [effectiveSteps],
  );
  const effectiveRehost = useMemo(
    () => overlayRehostFromChildRuns(run ?? {}, childAttemptsByAdapter.fileMetadata ?? []),
    [run, childAttemptsByAdapter],
  );
  const refreshChildAttempts = useCallback(async () => {
    if (!run?.tenantId || !run.steps?.length) return;
    const attempts = await listChildDatasetRunsForParent(
      run.tenantId,
      run.steps.map((step) => step.adapterKey),
      run.startedAt,
      run._id,
    );
    setChildAttemptsByAdapter(attempts);
  }, [run?.tenantId, run?.startedAt, run?.steps]);

  const handleStopChild = useCallback(async (childRunId: string) => {
    setBusy(true);
    setError(null);
    try {
      telemetry.trackEvent('legacy_migration_dataset_rerun_stop', { runId: childRunId });
      const result = await cancelRun(childRunId);
      if (result.status === 'cancelled') {
        setChildAttemptsByAdapter((current) => Object.fromEntries(
          Object.entries(current).map(([key, attempts]) => [
            key,
            attempts.map((child) => child._id === childRunId
              ? { ...child, status: 'cancelled', cancelRequested: true, finishedAt: new Date().toISOString() }
              : child),
          ]),
        ));
      }
      await fetchAndSyncRunById(childRunId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop re-run');
    } finally {
      setBusy(false);
    }
  }, []);

  const handleResume = useCallback(async () => {
    if (!runId) return;
    setBusy(true);
    setError(null);
    try {
      await resumeRun(runId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resume migration');
    } finally {
      setBusy(false);
    }
  }, [runId]);

  const openDatasetGrid = useCallback((adapterKey: string) => {
    if (!run?.tenantId) return;
    navigate('/legacy-migration/runs', {
      state: buildDatasetGridNavState(run.tenantId, adapterKey),
    });
  }, [navigate, run?.tenantId]);

  const handleRestartDataset = useCallback(async (adapterKey: string) => {
    if (!run) return;
    setBusy(true);
    setRetryingKey(adapterKey);
    setError(null);
    try {
      telemetry.trackEvent('legacy_migration_dataset_rerun_restart', { adapterKey });
      const { runId: newRunId } = await startRun(adapterKey, {
        tenantId: run.tenantId,
        mode: 'apply',
        selection: ['*'],
        bundleDeps: false,
        ...(run.isGlobal ? { parentRunId: run._id } : {}),
      });
      await fetchAndSyncRunById(newRunId);
      if (run.isGlobal) {
        await refreshChildAttempts();
        setOpenKey(adapterKey);
      } else {
        navigate(`/legacy-migration/runs/${newRunId}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restart dataset');
    } finally {
      setBusy(false);
      setRetryingKey(null);
    }
  }, [navigate, refreshChildAttempts, run]);

  const handleRetryFailed = useCallback(async (step: MigrationRunStepRow) => {
    if (!run) return;
    const retry = resolveFailedStepRetrySelection(step);
    if (!retry) return;
    setBusy(true);
    setRetryingKey(step.adapterKey);
    setError(null);
    try {
      telemetry.trackEvent('legacy_migration_dataset_rerun_started', {
        adapterKey: step.adapterKey,
        usesAllRowsFallback: String(retry.usesAllRowsFallback),
      });
      const { runId: newRunId } = await startRun(step.adapterKey, {
        tenantId: run.tenantId,
        mode: 'apply',
        selection: retry.selection,
        bundleDeps: false,
        parentRunId: run._id,
      });
      await fetchAndSyncRunById(newRunId);
      await refreshChildAttempts();
      setOpenKey(step.adapterKey);
    } catch (err) {
      telemetry.trackEvent('legacy_migration_dataset_rerun_failed', { adapterKey: step.adapterKey });
      setError(err instanceof Error ? err.message : 'Failed to retry failed rows');
    } finally {
      setBusy(false);
      setRetryingKey(null);
    }
  }, [refreshChildAttempts, run]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading migration report…
      </div>
    );
  }

  if (!run) {
    return (
      <div className="p-6 text-sm text-gray-600">
        <p>Run not found in local history yet.</p>
        <Link to="/legacy-migration/runs" className="text-orange-700 hover:underline">Back to Migration Runs</Link>
      </div>
    );
  }

  const effectiveRunStatus = run.cancelRequested && (run.status === 'running' || run.status === 'pending')
    ? 'cancelled'
    : run.status;
  const canResume = Boolean(run.isGlobal) && (effectiveRunStatus === 'cancelled' || effectiveRunStatus === 'failed');
  const isSingleDataset = !run.isGlobal;
  const datasetLabel = displayNameForDatasetKey(run.datasetKey);
  const heading = isSingleDataset ? `${datasetLabel} re-run` : 'Department Migration Dashboard';
  const isLive = effectiveRunStatus === 'running' || effectiveRunStatus === 'pending';
  const workerHeartbeatStale = isSingleDataset && isMigrationRunHeartbeatStale(run, nowMs);
  const isActivelyRunning = isLive && !workerHeartbeatStale;
  const selected = run.total || run.counts.selected || 0;
  const processed = run.processed ?? (run.counts.created + run.counts.updated + run.counts.skipped + run.counts.failed);
  const liveLine = formatStepProgressLine({
    phase: run.phase,
    processed: run.processed,
    total: run.total,
    message: run.message,
    displayName: datasetLabel,
  });
  const percent = isSingleDataset
    ? (isLive
      ? (typeof run.overallPercent === 'number'
        ? run.overallPercent
        : selected > 0
          ? Math.min(99, Math.round((processed / selected) * 100))
          : 5)
      : 100)
    : (run.overallPercent ?? 0);
  const displayCounts = isSingleDataset ? run.counts : effectiveSummary.counts;
  const dashboardStatusLabel = effectiveSummary.status === 'needs-attention'
    ? 'Needs attention'
    : effectiveSummary.status === 'running'
      ? 'In progress'
      : 'Completed';
  const remainingStepLabels = effectiveSummary.remainingDatasetKeys.map((key) =>
    effectiveSteps.find((step) => step.adapterKey === key)?.displayName ?? displayNameForDatasetKey(key));

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={() => navigate('/legacy-migration/runs')}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Migration Runs
        </button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold">{heading}</h1>
          {!isSingleDataset && (
            <p className="mt-1 text-xs text-gray-500">
              Department run <span className="font-mono text-gray-700">{run._id}</span>
              {latestDatasetAttempt ? (
                <> · Latest dataset attempt <span className="font-mono text-gray-700">{latestDatasetAttempt._id}</span> ({displayNameForDatasetKey(latestDatasetAttempt.datasetKey)})</>
              ) : null}
            </p>
          )}
          <p className="text-sm text-gray-500 mt-1">
            {workerHeartbeatStale
              ? 'Worker not responding · no recent progress update'
              : isSingleDataset && isLive
                ? (liveLine || `Applying ${datasetLabel}…`)
                : `${isSingleDataset ? effectiveRunStatus : dashboardStatusLabel} · ${run.mode}${isSingleDataset ? '' : ` · ${percent}%`}`}
            {run.finishedAt ? ` · parent finished ${new Date(run.finishedAt).toLocaleString()}` : ''}
          </p>
          <p className="text-sm text-gray-700 mt-1">
            {isSingleDataset ? (
              <>{displayCounts.created} created · {displayCounts.updated} updated · {displayCounts.skipped} skipped{displayCounts.failed > 0 ? ` · ${displayCounts.failed} failed` : ''}</>
            ) : (
              <>{effectiveSummary.completedDatasets} / {effectiveSteps.length} datasets clear{displayCounts.failed > 0 ? ` · ${displayCounts.failed} failed rows remain` : ' · no failed rows remain'}</>
            )}
            {(() => {
              const copyLine = formatRehostProgressLine({
                rehostStatus: effectiveRehost.rehostStatus ?? run.rehostStatus,
                rehostCounts: effectiveRehost.rehostCounts ?? run.rehostCounts,
              });
              return copyLine ? ` · ${copyLine}` : '';
            })()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isSingleDataset && isLive && (
            <button
              type="button"
              onClick={() => { void handleStopChild(run._id); }}
              disabled={busy || Boolean(run.cancelRequested)}
              className="px-3 py-1.5 text-sm border border-red-200 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50"
            >
              {run.cancelRequested ? 'Stopping…' : busy ? 'Stopping…' : 'Stop'}
            </button>
          )}
          {isSingleDataset && (effectiveRunStatus === 'cancelled' || effectiveRunStatus === 'failed') && (
            <button
              type="button"
              onClick={() => { void handleRestartDataset(run.datasetKey); }}
              disabled={busy}
              className="px-3 py-1.5 text-sm border border-orange-200 text-orange-800 rounded-md hover:bg-orange-50 disabled:opacity-50"
            >
              {busy ? 'Restarting…' : 'Restart'}
            </button>
          )}
          {canResume && (
            <button
              type="button"
              onClick={() => { void handleResume(); }}
              disabled={busy}
              className="px-3 py-1.5 text-sm border border-orange-200 text-orange-800 rounded-md hover:bg-orange-50 disabled:opacity-50"
            >
              {busy ? 'Resuming…' : 'Resume remaining'}
            </button>
          )}
        </div>
      </div>

      {!isSingleDataset && (
        <section className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className={`rounded-lg border p-3 ${effectiveSummary.status === 'completed' ? 'border-emerald-200 bg-emerald-50' : effectiveSummary.status === 'running' ? 'border-orange-200 bg-orange-50' : 'border-amber-300 bg-amber-50'}`}>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Effective status</div>
            <div className="mt-1 font-semibold text-gray-900">{dashboardStatusLabel}</div>
            <div className="mt-1 text-xs text-gray-600">
              {effectiveSummary.completedDatasets} / {effectiveSteps.length} datasets clear
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Effective result</div>
            <div className="mt-1 text-sm text-gray-800">
              {effectiveSummary.completedDatasets} / {effectiveSteps.length} datasets clear
            </div>
            <div className={`mt-1 text-xs ${displayCounts.failed > 0 ? 'text-amber-800' : 'text-emerald-700'}`}>
              {displayCounts.failed > 0 ? `${displayCounts.failed} rows still failed` : 'No failed rows remain'}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Remaining work</div>
            <div className="mt-1 text-sm text-gray-800">
              {remainingStepLabels.length > 0 ? `${remainingStepLabels.length} dataset(s)` : 'None'}
            </div>
            {remainingStepLabels.length > 0 && (
              <div className="mt-1 text-xs text-amber-800 break-words">{remainingStepLabels.join(', ')}</div>
            )}
          </div>
        </section>
      )}

      {error && (
        <div className="mb-4 px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">{error}</div>
      )}

      {isActivelyRunning && (
        <div className="mb-4 h-2 rounded-full bg-orange-100 overflow-hidden">
          <div
            className={`h-full bg-orange-500 ${percent < 8 ? 'animate-pulse' : ''}`}
            style={{ width: `${Math.max(8, percent)}%` }}
          />
        </div>
      )}

      {workerHeartbeatStale && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">No worker progress received</div>
            <div className="text-xs mt-0.5">
              The migration worker has not reported within two minutes. This run may have failed before processing began. Stop this run before restarting it.
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500 mb-3">
        {isSingleDataset
          ? `This ${datasetLabel} re-run does not change the original department-wide report.`
          : 'Open a failed tile to re-run that dataset only. The original department-wide report stays as history.'}
      </p>

      {isSingleDataset ? (
        <div className="rounded-lg border bg-white p-4 text-sm space-y-3 max-w-xl">
          <div className="flex items-center gap-2">
            {isActivelyRunning ? <Loader2 className="h-5 w-5 animate-spin text-orange-600" /> : null}
            {workerHeartbeatStale ? <AlertTriangle className="h-5 w-5 text-amber-600" /> : null}
            {effectiveRunStatus === 'completed' && run.counts.failed === 0 ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : null}
            {effectiveRunStatus === 'completed' && run.counts.failed > 0 ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : null}
            {effectiveRunStatus === 'failed' ? <XCircle className="h-4 w-4 text-red-600" /> : null}
            <span className="font-medium">
              {workerHeartbeatStale
                ? 'Worker not responding'
                : run.cancelRequested && isLive
                  ? 'Stopping…'
                  : isLive
                    ? 'In progress'
                  : effectiveRunStatus === 'completed'
                    ? 'Completed'
                    : effectiveRunStatus === 'cancelled'
                      ? 'Stopped'
                      : effectiveRunStatus}
            </span>
          </div>
          <p className="text-gray-600">
            {workerHeartbeatStale
              ? 'No worker heartbeat was received recently. The displayed counts are not advancing.'
              : isLive
                ? (liveLine || (run.phase === 'writing'
                  ? `Writing ${datasetLabel} to the tenant database.`
                  : `Reading ${datasetLabel} from Streamline. This can take several minutes.`))
              : effectiveRunStatus === 'cancelled'
                ? `${datasetLabel} stopped. Restart applies every row again.`
                : `${datasetLabel} finished.`}
          </p>
          {isLive && run.heartbeatAt ? (
            <p className="text-xs text-gray-400">
              Last worker update {new Date(run.heartbeatAt).toLocaleTimeString()}
            </p>
          ) : isLive ? (
            <p className="text-xs text-gray-400">
              Waiting for first worker update · started {new Date(run.startedAt).toLocaleTimeString()}
            </p>
          ) : null}
          <button
            type="button"
            className="text-xs px-2 py-1 border rounded-md hover:bg-gray-50"
            onClick={() => openDatasetGrid(run.datasetKey)}
          >
            Open {datasetLabel} grid
          </button>
        </div>
      ) : tiers.map((tier) => (
        <section key={tier.id} className="mb-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{tier.label}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {tier.steps.map((step) => {
              const original = run.steps?.find((s) => s.adapterKey === step.adapterKey) ?? step;
              const attempts = childAttemptsByAdapter[step.adapterKey] ?? [];
              const latestAttempt = attempts[0];
              const open = openKey === step.adapterKey;
              const live = step.status === 'running' || step.status === 'pending' || retryingKey === step.adapterKey;
              const stepStale = live && isStepHeartbeatStale(step.heartbeatAt, nowMs, undefined, step.startedAt);
              const failed = !live && (isCompletedWithRowFailures(step) || step.status === 'failed');
              return (
                <article
                  key={step.adapterKey}
                  className={`rounded-lg border bg-white p-3 text-sm ${stepStale ? 'border-amber-300' : live ? 'border-orange-300' : failed ? 'border-amber-300' : 'border-gray-200'}`}
                >
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => setOpenKey(open ? null : step.adapterKey)}
                  >
                    <div className="flex items-start gap-2">
                      {live && !stepStale ? <Loader2 className="h-4 w-4 text-orange-600 animate-spin shrink-0 mt-0.5" /> : null}
                      {stepStale ? <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" /> : null}
                      {!live && step.status === 'failed' ? <XCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" /> : null}
                      {!live && failed && step.status !== 'failed' ? <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" /> : null}
                      {!live && step.status === 'completed' && !failed ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> : null}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900">{step.displayName}</div>
                        <div className="text-[10px] text-gray-400 font-mono truncate">
                          {latestAttempt ? `Effective run ${latestAttempt._id}` : `Parent run ${run._id}`}
                          {attempts.length > 0 ? ` · ${attempts.length} rerun attempt${attempts.length === 1 ? '' : 's'}` : ''}
                        </div>
                        <div className={`text-xs ${stepStale ? 'text-amber-800' : live ? 'text-orange-800' : failed ? 'text-amber-800' : 'text-gray-500'}`}>
                          {stepStale
                            ? 'Worker not responding · no recent progress update'
                            : live
                              ? (formatStepProgressLine(step) || (latestChildByAdapter[step.adapterKey]?.phase === 'writing'
                                ? 'Writing to tenant database'
                                : 'Reading from Streamline'))
                            : step.status === 'cancelled'
                              ? `Stopped · ${step.processed ?? 0} / ${step.total ?? step.counts.selected} processed`
                              : `${step.status === 'completed' && step.counts.failed > 0 ? 'Needs attention' : step.status} · ${step.counts.created} created · ${step.counts.updated} updated${step.counts.skipped > 0 ? ` · ${step.counts.skipped} skipped` : ''}${step.counts.failed > 0 ? ` · ${step.counts.failed} failed` : ''}`}
                        </div>
                        {step.error ? <div className="text-xs text-red-600 mt-1">{formatMigrationFailureForAdmin(step.error)}</div> : null}
                      </div>
                    </div>
                  </button>
                  {open && (() => {
                    const retry = resolveFailedStepRetrySelection(step);
                    const failureSource = step;
                    return (
                    <div className="mt-3 border-t pt-2 space-y-2">
                      {retry ? (
                      <p className="text-xs text-gray-600">
                        {retry.usesAllRowsFallback
                          ? `This older run did not store failed row IDs. Re-run applies every ${step.displayName} row (not the 3-hour department-wide job). Already-migrated rows update in place.`
                          : `${retry.selection.length} failed row(s) will be re-applied.`}
                      </p>
                      ) : null}
                      {(failureSource.failedReasonSummary ?? []).length > 0 ? (
                        <ul className="text-xs text-amber-900 space-y-1">
                          {failureSource.failedReasonSummary!.map((reason) => (
                            <li key={reason.error}>{reason.count} × {formatMigrationFailureForAdmin(reason.error)}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-gray-500">
                          {step.counts.failed > 0
                            ? `Row failures were counted but reasons were not stored for the effective run. Re-run ${step.displayName} to capture the remaining failures.`
                            : 'No row failures.'}
                        </p>
                      )}
                      {(failureSource.failedRows ?? []).slice(0, 25).length > 0 && (
                        <p className="text-[11px] font-mono text-gray-600 break-all">
                          IDs: {(failureSource.failedRows ?? []).slice(0, 25).map((row) => row.legacyId).join(', ')}
                          {(failureSource.failedRows?.length ?? 0) > 25 ? '…' : ''}
                        </p>
                      )}
                      <div className="rounded-md border border-gray-200 bg-gray-50 p-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Attempt history</div>
                        <div className="space-y-1">
                          {attempts.map((attempt, index) => (
                            <div key={attempt._id} className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
                              <Link className="font-mono text-orange-700 hover:underline" to={`/legacy-migration/runs/${attempt._id}`}>
                                {attempt._id}
                              </Link>
                              <span className={attempt.counts.failed > 0 || attempt.status === 'failed' ? 'text-amber-800' : 'text-gray-600'}>
                                {index === 0 ? 'Latest · ' : ''}{attempt.status}
                                {attempt.status === 'completed' && attempt.counts.failed > 0 ? ' with failures' : ''}
                                {' · '}{attempt.counts.updated} updated · {attempt.counts.failed} failed · {new Date(attempt.startedAt).toLocaleString()}
                              </span>
                            </div>
                          ))}
                          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-500">
                            <span className="font-mono">{run._id}</span>
                            <span>Original department run · {original.status} · {original.counts.updated} updated · {original.counts.failed} failed</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="text-xs px-2 py-1 border rounded-md hover:bg-gray-50"
                          onClick={() => openDatasetGrid(step.adapterKey)}
                        >
                          Open {step.displayName} grid
                        </button>
                        {live && latestChildByAdapter[step.adapterKey]?._id && (
                          <button
                            type="button"
                            disabled={busy || Boolean(latestChildByAdapter[step.adapterKey]?.cancelRequested)}
                            className="text-xs px-2 py-1 border border-red-200 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50"
                            onClick={() => { void handleStopChild(latestChildByAdapter[step.adapterKey]!._id); }}
                          >
                            {latestChildByAdapter[step.adapterKey]?.cancelRequested ? 'Stopping…' : 'Stop'}
                          </button>
                        )}
                        {retry && !live && (
                          <button
                            type="button"
                            disabled={busy}
                            className="text-xs px-2 py-1 border border-orange-200 text-orange-800 rounded-md hover:bg-orange-50 disabled:opacity-50"
                            onClick={() => { void handleRetryFailed(original); }}
                          >
                            {retryingKey === step.adapterKey
                              ? 'Starting re-run…'
                              : retry.usesAllRowsFallback
                                ? `Re-run ${step.displayName} (all rows)`
                                : `Re-run failed ${step.displayName}`}
                          </button>
                        )}
                        {!live && (latestChildByAdapter[step.adapterKey]?.cancelRequested || latestChildByAdapter[step.adapterKey]?.status === 'cancelled' || latestChildByAdapter[step.adapterKey]?.status === 'failed') && (
                          <button
                            type="button"
                            disabled={busy}
                            className="text-xs px-2 py-1 border border-orange-200 text-orange-800 rounded-md hover:bg-orange-50 disabled:opacity-50"
                            onClick={() => { void handleRestartDataset(step.adapterKey); }}
                          >
                            Restart
                          </button>
                        )}
                      </div>
                    </div>
                    );
                  })()}
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
